import PoiCityRaceCityPoiView from '@utmb/types/view/PoiCityRaceCityPoiView';
import PoiCityRaceCityView from '@utmb/types/view/PoiCityRaceCityView';
import PoiCityRaceView from '@utmb/types/view/PoiCityRaceView';
import { CityMapsFilters, CityMapsMarker } from '@utmb/types/city-maps';

export interface Action {
    type: string;
}

export interface State {
    isLoaded: boolean;
    pois: PoiCityRaceView[];
    selectedRaceId?: number;
    selectedCity?: string;
    color?: string;
    colorIsDark: boolean;
    cities: PoiCityRaceCityView[];
    legend: CityMapsFilters;
    availableLegend: (keyof CityMapsFilters)[];
    markers: CityMapsMarker[];
}

export enum ActionKind {
    SET_POIS = 'SET_POIS',
    SELECT_RACE = 'SELECT_RACE',
    SELECT_CITY = 'SELECT_CITY',
    SET_LEGEND = 'SET_LEGEND',
}

export interface SetPoisAction extends Action {
    payload: PoiCityRaceView[];
}

export interface SetSelectedRaceAction extends Action {
    payload: number;
}

export interface SetSelectedCityAction extends Action {
    payload: string;
}

export interface SetLegendCityAction extends Action {
    payload: CityMapsFilters;
}

export const initialState: State = {
    isLoaded: false,
    pois: [],
    selectedRaceId: undefined,
    selectedCity: undefined,
    color: undefined,
    colorIsDark: true,
    cities: [],
    legend: {
        afterRaceMeal: true,
        bagRetrieval: true,
        bibCollection: true,
        bus: true,
        finish: true,
        info: true,
        medical: true,
        parking: true,
        physicalTherapy: true,
        refuelingStand: true,
        podium: true,
        polyline: false,
        showers: true,
        start: true,
        subway: true,
        taxi: true,
        toilets: true,
        train: true,
        tunnel: true,
    },
    availableLegend: [],
    markers: [],
};

function isSetPoisAction(action: Action): action is SetPoisAction {
    return action.type === ActionKind.SET_POIS;
}
function isSetSelectedRaceAction(action: Action): action is SetSelectedRaceAction {
    return action.type === ActionKind.SELECT_RACE;
}
function isSetSelectedCityAction(action: Action): action is SetSelectedCityAction {
    return action.type === ActionKind.SELECT_CITY;
}
function isSetLegendAction(action: Action): action is SetLegendCityAction {
    return action.type === ActionKind.SET_LEGEND;
}

export function reducer(state: State, action: Action) {
    let update: Partial<State> = {};
    if (isSetPoisAction(action)) {
        const pois = action.payload;

        if (pois.length) {
            const { raceId } = pois[0];
            update = getRaceUpdate(pois, state.legend, raceId);
        }

        update.pois = pois;
        update.isLoaded = true;
    }
    if (isSetSelectedRaceAction(action)) {
        const raceId = action.payload;

        update = getRaceUpdate(state.pois, state.legend, raceId);
    }
    if (isSetSelectedCityAction(action)) {
        const city = action.payload;

        const pois = getCityPois(state.cities, city);

        update.selectedCity = city;
        update.markers = getMarkers(pois, state.legend);
        update.availableLegend = getAvailableLegend(pois);
    }
    if (isSetLegendAction(action)) {
        const legend = action.payload;

        const pois = getCityPois(state.cities, state.selectedCity!);

        update.legend = legend;
        update.markers = getMarkers(pois, legend);
    }

    return {
        ...state,
        ...update,
    };
}

function getAvailableLegend(cityPois: PoiCityRaceCityPoiView[]): (keyof CityMapsFilters)[] {
    return [...new Set(cityPois.map(({ types }) => types).flat()), 'polyline'];
}

function getMarkers(cityPois: PoiCityRaceCityPoiView[], legend: CityMapsFilters): CityMapsMarker[] {
    return cityPois

        .map(({ types, ...rest }) => types.map((type) => ({ type, ...rest })))
        .flat()
        .filter(({ type }: CityMapsMarker) => legend[type]);
}

function getCityPois(cities: PoiCityRaceCityView[], selectedCity: string): PoiCityRaceCityPoiView[] {
    const { pois } = cities.find(({ name }) => name === selectedCity)!;
    return pois;
}

function getRaceUpdate(pois: PoiCityRaceView[], legend: CityMapsFilters, raceId: number): Partial<State> {
    const update: Partial<State> = {};
    const { color, cities, colorIsDark } = pois.find(({ raceId: id }) => id === raceId)!;
    update.selectedRaceId = raceId;
    update.cities = cities;
    update.color = color;
    update.colorIsDark = colorIsDark;

    if (cities.length) {
        const { name, pois: cityPois } = cities[0];
        update.selectedCity = name;
        update.availableLegend = getAvailableLegend(cityPois);
        update.markers = getMarkers(cityPois, legend);
    }

    return update;
}
